import React, { useEffect, useState } from "react";
import {
  X,
  Phone,
  Mail,
  MessageCircle,
  FileText,
  CheckSquare,
  Square,
  Trash2,
  Loader2,
  ArrowUpRight,
  Clock,
  User,
  Tag,
  ShoppingBag,
  RefreshCw,
  Plus,
} from "lucide-react";
import {
  adminGetActivities,
  adminAddActivity,
  adminUpdateActivity,
  adminDeleteActivity,
  adminContactSendEmail,
  adminContactLogWhatsApp,
} from "../lib/api";

const ACTIVITY_ICONS = {
  note: FileText,
  task: CheckSquare,
  call: Phone,
  email: Mail,
  whatsapp: MessageCircle,
  stage_change: RefreshCw,
  order: ShoppingBag,
  system: User,
};

const ACTIVITY_COLORS = {
  note: "#9ca3af",
  task: "#ffcc00",
  call: "#3b5bff",
  email: "#6b8bff",
  whatsapp: "#25D366",
  stage_change: "#a78bfa",
  order: "#d4ff3d",
  system: "#6b7280",
};

const TYPE_LABELS = {
  note: "Nota",
  task: "Task",
  call: "Chiamata",
  email: "Email",
  whatsapp: "WhatsApp",
  stage_change: "Cambio stage",
  order: "Ordine",
  system: "Sistema",
};

export default function ContactDrawer({ contact, onClose, onContactUpdated }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newAct, setNewAct] = useState({ type: "note", title: "", body: "", due_at: "" });
  const [emailModal, setEmailModal] = useState(false);
  const [waModal, setWaModal] = useState(false);

  const load = async () => {
    if (!contact) return;
    setLoading(true);
    try {
      const data = await adminGetActivities(contact.id);
      setActivities(data);
    } catch (e) {
      console.error("Failed to load activities:", e?.response?.data || e?.message);
    }
    finally { setLoading(false); }
  };

  // `load` reads only from state setters + a stable module import (adminGetActivities), so contact?.id is the only reactive dep.
  useEffect(() => { load(); }, [contact?.id]);

  if (!contact) return null;

  const handleAdd = async () => {
    if (!newAct.title.trim()) return;
    setCreating(true);
    try {
      const payload = {
        type: newAct.type,
        title: newAct.title.trim(),
        body: newAct.body.trim() || null,
        due_at: newAct.type === "task" && newAct.due_at ? new Date(newAct.due_at).toISOString() : null,
      };
      await adminAddActivity(contact.id, payload);
      setNewAct({ type: "note", title: "", body: "", due_at: "" });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore");
    } finally { setCreating(false); }
  };

  const toggleTask = async (activity) => {
    try {
      await adminUpdateActivity(activity.id, { completed: !activity.completed });
      load();
    } catch (e) {
      console.error("Failed to toggle task:", e?.response?.data || e?.message);
    }
  };

  const deleteActivity = async (activityId) => {
    if (!window.confirm("Eliminare questa attività?")) return;
    try {
      await adminDeleteActivity(activityId);
      load();
    } catch (e) {
      console.error("Failed to delete activity:", e?.response?.data || e?.message);
    }
  };

  const phoneNoPlus = contact.phone?.replace(/[^\d]/g, "") || "";
  const telUrl = contact.phone ? `tel:${contact.phone}` : null;

  return (
    <div className="fixed inset-0 z-[120] flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-xl bg-[#0f0f0f] border-l border-white/10 h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0f0f0f] border-b border-white/10 px-6 py-5 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="font-mono-label text-white/40 text-[10px]">Contatto</div>
            <div className="font-display text-white text-xl mt-1 truncate">{contact.name || contact.email}</div>
            <div className="text-white/50 text-xs mt-1 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1"><Mail size={11} /> {contact.email}</span>
              {contact.phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {contact.phone}</span>}
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <StageBadge stage={contact.stage} />
              {contact.total_spent > 0 && (
                <span className="text-[10px] uppercase tracking-widest border border-white/10 rounded-full px-2 py-0.5" style={{ color: "#d4ff3d" }}>
                  €{contact.total_spent.toFixed(0)} · {contact.orders_count} ordini
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white" aria-label="Chiudi">
            <X size={14} />
          </button>
        </div>

        {/* Quick actions */}
        <div className="px-6 py-4 border-b border-white/10 grid grid-cols-3 gap-2">
          <QuickAction
            href={telUrl}
            disabled={!telUrl}
            icon={<Phone size={14} />}
            label="Chiama"
            color="#3b5bff"
            onClick={() => telUrl && logQuickAction(contact.id, "call", `Chiamata a ${contact.name || contact.phone}`, load)}
          />
          <QuickAction
            onClick={() => phoneNoPlus && setWaModal(true)}
            disabled={!phoneNoPlus}
            icon={<MessageCircle size={14} />}
            label="WhatsApp"
            color="#25D366"
            dataTestId="contact-whatsapp-btn"
          />
          <QuickAction
            onClick={() => contact.email && setEmailModal(true)}
            disabled={!contact.email}
            icon={<Mail size={14} />}
            label="Email"
            color="#6b8bff"
            dataTestId="contact-email-btn"
          />
        </div>

        {/* Add activity form */}
        <div className="px-6 py-5 border-b border-white/10">
          <div className="font-mono-label text-white/40 text-[10px] mb-3">Aggiungi attività</div>
          <div className="flex gap-2 mb-3 flex-wrap">
            {["note", "task", "call", "email", "whatsapp"].map((t) => (
              <button
                key={t}
                onClick={() => setNewAct({ ...newAct, type: t })}
                className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                  newAct.type === t
                    ? "bg-white text-black border-white"
                    : "border-white/10 text-white/70 hover:text-white hover:border-white/30"
                }`}
              >
                {TYPE_LABELS[t]}
              </button>
            ))}
          </div>
          <input
            value={newAct.title}
            onChange={(e) => setNewAct({ ...newAct, title: e.target.value })}
            placeholder={newAct.type === "task" ? "Es. Richiamare per proposta" : "Titolo dell'attività"}
            className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30 mb-2"
          />
          <textarea
            value={newAct.body}
            onChange={(e) => setNewAct({ ...newAct, body: e.target.value })}
            placeholder="Dettagli / note (opzionale)"
            rows={2}
            className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30 mb-2 resize-none"
          />
          {newAct.type === "task" && (
            <input
              type="datetime-local"
              value={newAct.due_at}
              onChange={(e) => setNewAct({ ...newAct, due_at: e.target.value })}
              className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30 mb-2"
            />
          )}
          <button
            onClick={handleAdd}
            disabled={creating || !newAct.title.trim()}
            className={`btn-primary w-full justify-center ${!newAct.title.trim() || creating ? "opacity-50 cursor-not-allowed" : ""}`}
            style={{ padding: "10px 14px", fontSize: 13 }}
          >
            {creating ? <Loader2 size={13} className="animate-spin" /> : <><Plus size={13} /> Aggiungi</>}
          </button>
        </div>

        {/* Timeline */}
        <div className="px-6 py-5">
          <div className="font-mono-label text-white/40 text-[10px] mb-4">Timeline attività</div>
          {loading ? (
            <div className="text-white/50 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Caricamento...</div>
          ) : activities.length === 0 ? (
            <div className="text-white/40 text-sm py-8 text-center">Nessuna attività ancora. Inizia aggiungendone una sopra.</div>
          ) : (
            <ul className="space-y-3">
              {activities.map((a) => {
                const Icon = ACTIVITY_ICONS[a.type] || FileText;
                const color = ACTIVITY_COLORS[a.type] || "#9ca3af";
                return (
                  <li key={a.id} className="bg-[#141414] border border-white/10 rounded-xl p-4 group">
                    <div className="flex items-start gap-3">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: `${color}15`, border: `1px solid ${color}40` }}
                      >
                        <Icon size={14} style={{ color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[9px] uppercase tracking-widest text-white/40">{TYPE_LABELS[a.type]}</span>
                              {a.completed && <span className="text-[9px] uppercase tracking-widest text-lime" style={{ color: "#d4ff3d" }}>✓ Completato</span>}
                            </div>
                            <div className={`text-white text-sm mt-1 ${a.completed ? "line-through text-white/50" : ""}`}>{a.title}</div>
                            {a.body && <div className="text-white/60 text-xs mt-1 whitespace-pre-wrap">{a.body}</div>}
                            {a.due_at && (
                              <div className="text-[10px] text-white/50 mt-2 inline-flex items-center gap-1">
                                <Clock size={9} /> {new Date(a.due_at).toLocaleString("it-IT")}
                              </div>
                            )}
                            <div className="text-[10px] text-white/30 mt-2">
                              {new Date(a.created_at).toLocaleString("it-IT")}
                              {a.created_by === "system" && " · Sistema"}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {a.type === "task" && (
                              <button
                                onClick={() => toggleTask(a)}
                                className="p-1.5 rounded hover:bg-white/5 text-white/60 hover:text-white"
                                aria-label="Toggle completed"
                              >
                                {a.completed ? <CheckSquare size={13} /> : <Square size={13} />}
                              </button>
                            )}
                            {a.created_by !== "system" && (
                              <button
                                onClick={() => deleteActivity(a.id)}
                                className="p-1.5 rounded hover:bg-red-500/10 text-white/50 hover:text-red-400"
                                aria-label="Elimina"
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {emailModal && (
          <EmailComposeModal
            contact={contact}
            onClose={() => setEmailModal(false)}
            onSent={() => { setEmailModal(false); load(); }}
          />
        )}
        {waModal && (
          <WhatsAppComposeModal
            contact={contact}
            phoneNoPlus={phoneNoPlus}
            onClose={() => setWaModal(false)}
            onSent={() => { setWaModal(false); load(); }}
          />
        )}
      </div>
    </div>
  );
}

/* ─────────────── Email compose modal ─────────────── */
function EmailComposeModal({ contact, onClose, onSent }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState(`Ciao ${contact.name?.split(" ")[0] || ""},\n\n`);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const send = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true); setError(null);
    try {
      // Convert plain-text newlines to <br> in the html body
      const html_body = body.trim().split(/\n\n+/).map(p => `<p>${p.replace(/\n/g, "<br>")}</p>`).join("\n");
      await adminContactSendEmail(contact.id, { subject: subject.trim(), html_body });
      onSent();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center px-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-[#0f0f0f] border border-white/15 rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-mono-label text-white/40 text-[10px]">Invia email</div>
            <div className="font-display text-white text-lg mt-1">A: {contact.email}</div>
            <div className="text-white/40 text-[11px] mt-1">Da: Frank Gheri &lt;frank@mydigitalaction.it&gt;</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white">
            <X size={14} />
          </button>
        </div>
        <label className="block text-white/70 text-[11px] uppercase tracking-widest font-mono mb-2">Oggetto</label>
        <input value={subject} onChange={(e) => setSubject(e.target.value)}
          className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30 mb-4"
          placeholder="Es. Aggiornamento sul tuo corso"
          data-testid="contact-email-subject" />
        <label className="block text-white/70 text-[11px] uppercase tracking-widest font-mono mb-2">Messaggio</label>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8}
          className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30 resize-y"
          data-testid="contact-email-body" />
        <div className="text-white/40 text-[10px] mt-1">Firma automatica di Frank sarà aggiunta in fondo.</div>
        {error && <div className="mt-3 text-red-400 text-sm">{error}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/70 hover:text-white">Annulla</button>
          <button onClick={send} disabled={sending || !subject.trim() || !body.trim()}
            className="text-xs px-4 py-2 rounded-lg font-semibold disabled:opacity-50 inline-flex items-center gap-2"
            style={{ background: "#d4ff3d", color: "#0a0a0a" }}
            data-testid="contact-email-send">
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
            {sending ? "Invio..." : "Invia email"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────── WhatsApp compose modal ─────────────── */
function WhatsAppComposeModal({ contact, phoneNoPlus, onClose, onSent }) {
  const templates = [
    { label: "Presentazione", text: `Ciao ${contact.name?.split(" ")[0] || ""}! Ti scrivo da MyDigitalAction. Volevo ringraziarti per averci contattato — sono qui se hai qualsiasi domanda sui nostri corsi.` },
    { label: "Reminder call", text: `Ciao ${contact.name?.split(" ")[0] || ""}, ricordati della nostra call di orientamento. Se ti serve spostarla, dimmi pure quale slot preferisci.` },
    { label: "Follow-up post-call", text: `Ciao ${contact.name?.split(" ")[0] || ""}, grazie per la call! Come promesso ti riepilogo qui i punti chiave e ti mando il link per procedere quando sei pronto.` },
    { label: "Bacheca corsi", text: `Ciao ${contact.name?.split(" ")[0] || ""}, ti giro il link con tutti i corsi in aula disponibili: https://mydigitalaction.it/#corsi` },
    { label: "Libero", text: "" },
  ];
  const [message, setMessage] = useState(templates[0].text);
  const [sending, setSending] = useState(false);

  const openWhatsApp = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      // Log first, then open WhatsApp deep link in new tab
      await adminContactLogWhatsApp(contact.id, message);
      const url = `https://api.whatsapp.com/send?phone=${phoneNoPlus}&text=${encodeURIComponent(message)}`;
      window.open(url, "_blank");
      onSent();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center px-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-[#0f0f0f] border border-white/15 rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-mono-label text-white/40 text-[10px]">Invia WhatsApp</div>
            <div className="font-display text-white text-lg mt-1">A: {contact.phone}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white">
            <X size={14} />
          </button>
        </div>
        <label className="block text-white/70 text-[11px] uppercase tracking-widest font-mono mb-2">Template</label>
        <div className="flex flex-wrap gap-2 mb-4">
          {templates.map((t) => (
            <button key={t.label} type="button" onClick={() => setMessage(t.text)}
              className="text-[11px] px-3 py-1.5 rounded-full border border-white/10 text-white/70 hover:border-white/30 hover:text-white"
              data-testid={`wa-template-${t.label}`}>
              {t.label}
            </button>
          ))}
        </div>
        <label className="block text-white/70 text-[11px] uppercase tracking-widest font-mono mb-2">Messaggio</label>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6}
          className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30 resize-y"
          data-testid="contact-whatsapp-body" />
        <div className="text-white/40 text-[10px] mt-1">Cliccando "Invia" si aprirà WhatsApp con il messaggio pre-compilato in una nuova scheda.</div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/70 hover:text-white">Annulla</button>
          <button onClick={openWhatsApp} disabled={sending || !message.trim()}
            className="text-xs px-4 py-2 rounded-lg font-semibold disabled:opacity-50 inline-flex items-center gap-2"
            style={{ background: "#25D366", color: "#0a0a0a" }}
            data-testid="contact-whatsapp-send">
            {sending ? <Loader2 size={12} className="animate-spin" /> : <MessageCircle size={12} />}
            {sending ? "Apertura..." : "Apri WhatsApp"}
          </button>
        </div>
      </div>
    </div>
  );
}

async function logQuickAction(contactId, type, title, refresh) {
  try {
    await adminAddActivity(contactId, { type, title, body: null });
    refresh();
  } catch (e) {
    console.error("Failed to log quick action:", e?.response?.data || e?.message);
  }
}

function QuickAction({ icon, label, color, href, disabled, external, onClick, dataTestId }) {
  const className = `flex items-center justify-center gap-1.5 py-2.5 rounded-lg border text-xs font-medium transition-all ${
    disabled
      ? "border-white/5 text-white/25 cursor-not-allowed"
      : "border-white/10 text-white hover:border-white/30"
  }`;
  const style = disabled ? {} : { color };
  if (!href) {
    return (
      <button disabled={disabled} onClick={onClick} className={className} style={style} data-testid={dataTestId}>
        {icon} {label}
      </button>
    );
  }
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
      onClick={onClick}
      className={className}
      style={style}
      data-testid={dataTestId}
    >
      {icon} {label}
    </a>
  );
}

function StageBadge({ stage }) {
  const map = {
    lead: { color: "#8b8b8b", label: "Lead" },
    contacted: { color: "#6b8bff", label: "Contattato" },
    qualified: { color: "#3b5bff", label: "Qualificato" },
    proposal: { color: "#ffcc00", label: "Proposta" },
    won: { color: "#d4ff3d", label: "Cliente" },
    lost: { color: "#ff6b6b", label: "Perso" },
  };
  const s = map[stage] || map.lead;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border"
      style={{ color: s.color, borderColor: `${s.color}55` }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} /> {s.label}
    </span>
  );
}
